import { Avatar } from "@package/subframe/components/Avatar";
import { Badge } from "@package/subframe/components/Badge";
import { Button } from "@package/subframe/components/Button";
import { Checkbox } from "@package/subframe/components/Checkbox";
import { Select } from "@package/subframe/components/Select";
import { Table } from "@package/subframe/components/Table";
import { TextField } from "@package/subframe/components/TextField";
import {
  FeatherBuilding,
  FeatherKey,
  FeatherPlus,
  FeatherSearch,
  FeatherUser,
} from "@subframe/core";
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { useState } from "react";

import { Pagination } from "@/components/ui/pagination";
import { Spinner } from "@/components/ui/spinner";
import { TABLE_MIN_WIDTH } from "@/components/ui/table-scroll-container";
import { env } from "@/config/env";
import { useAuth } from "@/contexts/AuthContext";
import { useNursery } from "@/contexts/NurseryContext";
import { BulkSetPinDialog } from "@/features/staff/BulkSetPinDialog";
import type { ListUsersQueryControllerListRole } from "@/generated/model/listUsersQueryControllerListRole";
import type { UserResponseDto } from "@/generated/model/userResponseDto";
import { useListUsersQueryControllerList } from "@/generated/users/users";

/**
 * The old Staff list. While the People Hub is switched off this is the live
 * page; once it's on, the People directory takes over and old links still land.
 * The list endpoint scopes a manager or org admin to the people they manage.
 */
export const Route = createFileRoute("/_authenticated/settings/staff/")({
  beforeLoad: () => {
    if (env.VITE_PEOPLE_HUB_ENABLED) {
      throw redirect({ to: "/people" });
    }
  },
  component: StaffPage,
});

const DEFAULT_PAGE_SIZE = 50;

function getDisplayName(user: UserResponseDto): string {
  if (user.firstName && user.lastName) {
    return `${user.firstName} ${user.lastName}`;
  }
  if (user.email) {
    const atIndex = user.email.indexOf("@");
    if (atIndex !== -1) {
      return user.email.substring(0, atIndex).replace(/_/g, " ");
    }
    return user.email;
  }
  return "Unknown";
}

function getInitials(name: string): string {
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

function getRoleLabel(role: string): string {
  switch (role) {
    case "MANAGER":
      return "Manager";
    case "DEPUTY_MANAGER":
      return "Deputy Manager";
    case "LEADER":
      return "Leader";
    case "PRACTITIONER":
      return "Practitioner";
    default:
      return role;
  }
}

function StaffPage() {
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const { nurseries } = useNursery();

  const [searchQuery, setSearchQuery] = useState("");
  const [roleFilter, setRoleFilter] =
    useState<ListUsersQueryControllerListRole | null>(null);
  const [nurseryFilter, setNurseryFilter] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  // Selection for bulk temporary-PIN assignment; persists across pages.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkPinOpen, setBulkPinOpen] = useState(false);

  // Fetch users with server-side filtering and pagination
  const { data: usersResponse, isLoading } = useListUsersQueryControllerList({
    withNurseryAccessOnly: true,
    nurseryId: nurseryFilter ?? undefined,
    role: roleFilter ?? undefined,
    search: searchQuery || undefined,
    limit: pageSize,
    offset: (currentPage - 1) * pageSize,
  });

  const responseData = usersResponse?.data;
  const users = responseData?.data ?? [];
  const total = responseData?.total ?? 0;

  // Helper to get user's roles for display
  const getUserRoles = (user: UserResponseDto): string[] => {
    if (!user.nurseryAccess || user.nurseryAccess.length === 0) {
      return [];
    }
    // Get unique roles
    const roles = [...new Set(user.nurseryAccess.map((na) => na.role))];
    return roles;
  };

  // Helper to get user's nursery names for display
  const getUserNurseryNames = (user: UserResponseDto): string[] => {
    if (!user.nurseryAccess || user.nurseryAccess.length === 0) {
      return [];
    }
    // Get unique nursery names from the response
    const names = [
      ...new Set(user.nurseryAccess.map((na) => na.nurseryName ?? "Unknown")),
    ];
    return names;
  };

  // The bulk temporary-PIN action never targets the caller's own account —
  // changing your own PIN is the self-service flow (the server rejects it too).
  const isSelectable = (user: UserResponseDto) => user.id !== currentUser?.id;
  const selectableOnPage = users.filter(isSelectable);
  const allOnPageSelected =
    selectableOnPage.length > 0 &&
    selectableOnPage.every((u) => selectedIds.has(u.id));

  function toggleUser(userId: string, checked: boolean) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(userId);
      } else {
        next.delete(userId);
      }
      return next;
    });
  }

  function toggleAllOnPage(checked: boolean) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      for (const user of selectableOnPage) {
        if (checked) {
          next.add(user.id);
        } else {
          next.delete(user.id);
        }
      }
      return next;
    });
  }

  return (
    <div className="flex min-h-full w-full flex-col items-start gap-8 bg-white animate-fade-in pb-8">
      {/* Header Section */}
      <div className="flex w-full flex-col items-start gap-1">
        <div className="flex w-full flex-wrap items-center gap-4">
          <div className="flex grow shrink-0 basis-0 flex-col items-start gap-1">
            <span className="w-full whitespace-nowrap text-metrics font-metrics text-primary-navy-800">
              Staff management
            </span>
            <span className="text-body font-body text-subtext-color">
              Add and manage staff members and their permissions
            </span>
          </div>
        </div>
      </div>

      {/* Filters Section */}
      <div className="flex w-full flex-col items-start gap-6">
        <div className="flex w-full flex-col gap-4">
          {/* Search, Filters, and Add Button */}
          <div className="flex w-full flex-col gap-4 lg:flex-row lg:items-center lg:gap-6">
            <TextField
              className="h-auto w-full lg:w-72 lg:flex-none"
              variant="outline"
              size="base"
              icon={<FeatherSearch />}
            >
              <TextField.Input
                placeholder="Search staff..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </TextField>
            <div className="flex grow shrink-0 basis-0 flex-wrap items-center gap-4">
              <span className="text-body-bold font-body-bold text-default-font hidden lg:inline">
                Filter by
              </span>
              <div className="flex grow shrink-0 basis-0 flex-wrap items-center gap-2">
                <Select
                  placeholder="All roles"
                  icon={<FeatherUser />}
                  value={roleFilter ?? undefined}
                  onValueChange={(value) => {
                    setRoleFilter(
                      value === "all" || value === ""
                        ? null
                        : (value as ListUsersQueryControllerListRole),
                    );
                    setCurrentPage(1);
                  }}
                >
                  <Select.Item value="all">All roles</Select.Item>
                  <Select.Item value="MANAGER">Manager</Select.Item>
                  <Select.Item value="DEPUTY_MANAGER">
                    Deputy Manager
                  </Select.Item>
                  <Select.Item value="LEADER">Leader</Select.Item>
                  <Select.Item value="PRACTITIONER">Practitioner</Select.Item>
                </Select>
                <Select
                  placeholder="All locations"
                  icon={<FeatherBuilding />}
                  value={nurseryFilter ?? undefined}
                  onValueChange={(value) => {
                    setNurseryFilter(
                      value === "all" || value === "" ? null : value,
                    );
                    setCurrentPage(1);
                  }}
                >
                  <Select.Item value="all">All locations</Select.Item>
                  {nurseries.map((nursery) => (
                    <Select.Item key={nursery.id} value={nursery.id}>
                      {nursery.name}
                    </Select.Item>
                  ))}
                </Select>
              </div>
            </div>
            <Button
              className="w-full lg:w-auto"
              variant="brand-primary"
              icon={<FeatherPlus />}
              onClick={() => navigate({ to: "/settings/staff/new" })}
            >
              Add staff member
            </Button>
          </div>

          {selectedIds.size > 0 && (
            <div className="flex w-full flex-wrap items-center justify-between gap-3 rounded-md bg-secondary-beige-50 px-4 py-3">
              <span className="text-body font-body text-default-font">
                {selectedIds.size} staff member
                {selectedIds.size === 1 ? "" : "s"} selected
              </span>
              <div className="flex items-center gap-3">
                <Button
                  variant="neutral-secondary"
                  onClick={() => setSelectedIds(new Set())}
                >
                  Clear selection
                </Button>
                <Button
                  variant="brand-primary"
                  icon={<FeatherKey />}
                  onClick={() => setBulkPinOpen(true)}
                >
                  Set temporary PIN
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Table Section */}
        {isLoading ? (
          <div className="flex w-full items-center justify-center py-12">
            <Spinner className="h-8 w-8 text-primary" />
          </div>
        ) : users.length === 0 ? (
          <div className="flex w-full items-center justify-center py-12">
            <span className="text-body font-body text-subtext-color">
              {total === 0 && !searchQuery && !roleFilter && !nurseryFilter
                ? "No staff members yet. Add your first staff member to get started."
                : "No staff members match your filters."}
            </span>
          </div>
        ) : (
          <>
            <div className="flex w-full flex-col items-start bg-default-background overflow-auto pb-8">
              <Table
                className={TABLE_MIN_WIDTH.md}
                header={
                  <Table.HeaderRow>
                    <Table.HeaderCell className="w-10">
                      <Checkbox
                        checked={allOnPageSelected}
                        onCheckedChange={toggleAllOnPage}
                        aria-label="Select all staff on this page"
                      />
                    </Table.HeaderCell>
                    <Table.HeaderCell>Staff member</Table.HeaderCell>
                    <Table.HeaderCell>Role</Table.HeaderCell>
                    <Table.HeaderCell>Locations</Table.HeaderCell>
                    <Table.HeaderCell>Status</Table.HeaderCell>
                  </Table.HeaderRow>
                }
              >
                {users.map((user) => {
                  const displayName = getDisplayName(user);
                  const initials = getInitials(displayName);
                  const username = user.email ? user.email.split("@")[0] : "";
                  const roles = getUserRoles(user);
                  const nurseryNames = getUserNurseryNames(user);

                  return (
                    <Table.Row
                      key={user.id}
                      clickable
                      className="cursor-pointer"
                      onClick={() =>
                        navigate({
                          to: "/settings/staff/$id/edit",
                          params: { id: user.id },
                        })
                      }
                    >
                      <Table.Cell
                        className="w-10"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {isSelectable(user) && (
                          <Checkbox
                            checked={selectedIds.has(user.id)}
                            onCheckedChange={(checked) =>
                              toggleUser(user.id, checked)
                            }
                            aria-label={`Select ${displayName}`}
                          />
                        )}
                      </Table.Cell>
                      <Table.Cell>
                        <div className="flex items-center gap-3">
                          <Avatar
                            variant={user.isActive ? "brand" : "neutral"}
                            size="small"
                          >
                            {initials}
                          </Avatar>
                          <div className="flex flex-col items-start">
                            <span className="text-body-bold font-body-bold text-default-font">
                              {displayName}
                            </span>
                            <span className="text-caption font-caption text-subtext-color">
                              {username || "No email"}
                            </span>
                          </div>
                        </div>
                      </Table.Cell>
                      <Table.Cell>
                        <div className="flex flex-wrap gap-1">
                          {roles.length > 0 ? (
                            roles.map((role, index) => (
                              <Badge key={index} variant="neutral">
                                {getRoleLabel(role)}
                              </Badge>
                            ))
                          ) : (
                            <span className="text-body font-body text-subtext-color">
                              No role
                            </span>
                          )}
                        </div>
                      </Table.Cell>
                      <Table.Cell>
                        <span className="text-body font-body text-default-font">
                          {nurseryNames.length > 0
                            ? nurseryNames.join(", ")
                            : "No location"}
                        </span>
                      </Table.Cell>
                      <Table.Cell>
                        <Badge variant={user.isActive ? "success" : "neutral"}>
                          {user.isActive ? "Active" : "Inactive"}
                        </Badge>
                      </Table.Cell>
                    </Table.Row>
                  );
                })}
              </Table>
            </div>

            {/* Pagination */}
            {total > 0 && (
              <Pagination
                page={currentPage}
                pageSize={pageSize}
                totalCount={total}
                itemNoun="staff"
                onPageChange={setCurrentPage}
                onPageSizeChange={(size) => {
                  setPageSize(size);
                  setCurrentPage(1);
                }}
              />
            )}
          </>
        )}
      </div>

      <BulkSetPinDialog
        userIds={[...selectedIds]}
        open={bulkPinOpen}
        onOpenChange={setBulkPinOpen}
        onSuccess={() => setSelectedIds(new Set())}
      />
    </div>
  );
}
